import { Platform } from 'react-native';

/**
 * Helper to apply CSS responsive classes via data attributes.
 * On native platforms, returns empty object (no-op).
 * 
 * Usage:
 *   <View style={styles.heroSplit} {...responsive('hero-split')}>
 * 
 * This renders on web as:
 *   <div data-responsive="hero-split" ...>
 * 
 * Which CSS can target:
 *   [data-responsive="hero-split"] { ... }
 */

export type ResponsiveClass =
  // Hero section - layout
  | 'hero-section'
  | 'hero-split'
  | 'hero-left'
  | 'hero-right'
  | 'hero-description'
  | 'hero-meta'
  | 'hero-ctas'
  // Hero section - typography
  | 'hero-title'
  | 'hero-rotating-word'
  | 'hero-underline'
  // Section titles
  | 'section-title'
  | 'section-title-how'
  | 'cta-title'
  // Header
  | 'header-inline-nav'
  | 'header-hamburger'
  | 'header-title'
  | 'header-subtitle'
  | 'header-subtitle-expanded'
  | 'header-logo'
  // Footer
  | 'footer-container'
  | 'footer-top-row'
  | 'footer-brand'
  | 'footer-brand-title'
  | 'footer-center-links'
  | 'footer-link-column'
  | 'footer-column-heading'
  | 'footer-social-icons'
  | 'footer-social-icon'
  | 'footer-separator'
  | 'footer-bottom-row'
  | 'footer-copyright'
  | 'footer-legal-links'
  | 'footer-nav-link'
  // Sources section
  | 'sources-card'
  | 'source-logo'
  | 'source-logo-chemrxiv'
  | 'source-logo-pubmed'
  | 'sources-logos-container'
  | 'source-logo-wrapper'
  // Digest cards
  | 'digest-card'
  // Digest preview
  | 'digest-preview-mobile'
  | 'digest-preview-desktop'
  // CTA section
  | 'cta-band'
  | 'cta-content'
  | 'cta-left-column'
  | 'cta-right-column'
  | 'cta-buttons'
  // Grids
  | 'grid-3-2-1'
  | 'grid-4-2-1'
  | 'grid-3-1-1'
  | 'grid-4-1-1'
  // Visibility
  | 'hide-mobile'
  | 'hide-desktop'
  // Digest header
  | 'digest-header-title-row'
  | 'digest-header-title'
  | 'digest-header-buttons'
  // Settings page
  | 'settings-header'
  | 'settings-form-grid'
  | 'settings-form-span-all'
  // Paper card
  | 'paper-card-content'
  | 'paper-card-metadata'
  // Processing modal
  | 'processing-modal-layout'
  | 'processing-modal-left'
  | 'processing-modal-right';

/**
 * Apply a responsive CSS class to a component.
 * Returns props to spread onto the component.
 */
export function responsive(className: ResponsiveClass): { dataSet?: { responsive: string } } {
  if (Platform.OS !== 'web') {
    return {};
  }
  return {
    dataSet: { responsive: className },
  };
}

/**
 * Apply multiple responsive CSS classes.
 * Note: This uses space-separated values in the data attribute.
 * 
 * CSS selector for multiple classes:
 *   [data-responsive~="hero-split"]  (matches if contains "hero-split")
 */
export function responsiveMultiple(...classNames: ResponsiveClass[]): { dataSet?: { responsive: string } } {
  if (Platform.OS !== 'web') {
    return {};
  }
  return {
    dataSet: { responsive: classNames.join(' ') },
  };
}

/**
 * Check if we're on web platform.
 * Useful for conditional rendering that's web-specific.
 */
export const isWeb = Platform.OS === 'web';
